import React, { useCallback, useMemo } from 'react';
import styled, { css } from 'styled-components';
import { ISelectOption } from 'components/Select/types';
import { theme, toRem } from 'design';

interface IButtonTogglesProps {
  values?: string[];
  options: ISelectOption<string>[];
  disabled?: boolean;
  onChange?: (value: string[]) => void;
}

const Layout = styled.div`
  display: flex;
  flex-flow: row wrap;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: 100%;
  padding: 10px;
  gap: ${toRem(4)};

  background-color: ${theme.color.white};
  border: ${toRem(1)} solid ${theme.color.neutral[200]};
  border-radius: 5.5px;

  &:hover {
    border-color: ${theme.color.neutral[400]};
  }
`;

interface LabelButtonProps {
  selected?: boolean;
}

const LabelButton = styled.button<LabelButtonProps>`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  width: ${toRem(30)} !important;
  padding: 5.5px 0 !important;
  font-size: ${toRem(10)};

  background-color: ${theme.color.neutral[100]};
  border-radius: 2px;

  ${({ disabled }) =>
    disabled
      ? css`
          cursor: auto;
        `
      : css`
          &:hover,
          &:focus-visible {
            background-color: ${theme.color.neutral[200]};
          }
        `}

  ${({ selected, disabled }) =>
    selected &&
    css`
      color: ${theme.color.white};
      background-color: ${theme.color.secondary[400]};

      &:hover,
      &:focus-visible {
        background-color: ${theme.color.secondary[400]};
        opacity: ${disabled ? '100%' : '80%'};
      }
    `}
`;

const ButtonToggles: React.FC<IButtonTogglesProps> = ({ values = [], options, onChange, disabled }: IButtonTogglesProps) => {
  const handleChange = useCallback(
    (selectedValue: string) => {
      if (onChange) {
        const newValues = values.filter((val) => val !== selectedValue);

        if (newValues.length === values.length) newValues.push(selectedValue);

        onChange(newValues);
      }
    },
    [values]
  );

  const toggleOptions = useMemo(
    () =>
      options.map((option) => (
        <LabelButton type="button" key={option.value} selected={values.some((val) => val === option.value)} onClick={() => handleChange(option.value)} disabled={disabled}>
          {option.label}
        </LabelButton>
      )),
    [options, values, handleChange]
  );

  return <Layout>{toggleOptions}</Layout>;
};

export default ButtonToggles;
